// Base HTTP client. Stores session token in localStorage, sends as X-Session-Token.

const BASE = '/server/api';
const TOKEN_KEY = 'litigate_token';
const TENANT_KEY = 'litigate.tenantId';

export type ApiError = { code: string; message: string; details?: unknown };

// ---- Token ----
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

// ---- Tenant ----
// Catalyst ROWIDs are 16–19-digit BIGINTs that overflow JS Number. Store and
// forward the id as a string end-to-end so the BIGINT comparison on the
// server (WHERE tenant_id = ?) stays exact.
let activeTenantId: string | null = null;

export function setActiveTenantId(id: string | number | null) {
  if (id == null) {
    activeTenantId = null;
    localStorage.removeItem(TENANT_KEY);
    return;
  }
  const s = String(id);
  activeTenantId = s;
  localStorage.setItem(TENANT_KEY, s);
}

export function getActiveTenantId(): string | null {
  if (activeTenantId != null) return activeTenantId;
  const stored = localStorage.getItem(TENANT_KEY);
  if (stored) {
    activeTenantId = stored;
    return activeTenantId;
  }
  return null;
}

// ---- Fetch wrapper ----
export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {});
  headers.set('Accept', 'application/json');
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json');

  const token = getToken();
  if (token) headers.set('X-Session-Token', token);

  const tid = getActiveTenantId();
  if (tid != null) headers.set('X-Tenant-Id', tid);

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  if (res.status === 401) {
    // Session invalid / expired — clear local state and signal the app
    setToken(null);
    setActiveTenantId(null);
    window.dispatchEvent(new Event('litigate:logout'));
  }

  if (!res.ok) {
    let payload: { error?: ApiError } = {};
    try { payload = await res.json(); } catch { /* noop */ }
    const err = new Error(payload?.error?.message || res.statusText) as Error & { code?: string; status?: number; details?: unknown };
    err.code = payload?.error?.code || `http_${res.status}`;
    err.status = res.status;
    err.details = payload?.error?.details;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export const http = {
  get:  <T>(p: string)            => api<T>(p),
  post: <T>(p: string, body?: unknown) => api<T>(p, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put:  <T>(p: string, body?: unknown) => api<T>(p, { method: 'PUT',  body: body ? JSON.stringify(body) : undefined }),
  patch:<T>(p: string, body?: unknown) => api<T>(p, { method: 'PATCH',body: body ? JSON.stringify(body) : undefined }),
  del:  <T>(p: string)            => api<T>(p, { method: 'DELETE' }),
};

/**
 * Authenticated fetch that returns a Blob. Used for inline document
 * previews — <img> / <iframe> tags can't send the X-Session-Token header,
 * so we fetch the bytes ourselves, wrap them in a blob URL, and feed that
 * to the renderer. Caller is responsible for URL.revokeObjectURL on cleanup.
 */
export async function fetchAuthorisedBlob(path: string): Promise<Blob> {
  const headers: HeadersInit = { Accept: '*/*' };
  const token = getToken();
  if (token) (headers as Record<string, string>)['X-Session-Token'] = token;
  const tid = getActiveTenantId();
  if (tid != null) (headers as Record<string, string>)['X-Tenant-Id'] = tid;

  // path may be absolute (presigned Stratus URL) or relative. Don't prefix BASE
  // on absolute URLs — Stratus presigned links live on a separate origin.
  const url = /^https?:\/\//i.test(path) ? path : `${BASE}${path}`;
  const res = await fetch(url, { headers });
  if (res.status === 401) {
    setToken(null); setActiveTenantId(null);
    window.dispatchEvent(new Event('litigate:logout'));
  }
  if (!res.ok) {
    const err = new Error(`Download failed (${res.status} ${res.statusText})`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return res.blob();
}

/**
 * Multipart-form upload with progress events. Plain fetch() can't surface
 * XHR upload progress, so we drop down to XMLHttpRequest for this one helper
 * and surface byte-level progress for the FileUpload UI. Session token and
 * tenant header are added the same way as api().
 */
export function xhrUpload<T>(
  path: string,
  form: FormData,
  opts: { onProgress?: (pct: number) => void } = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}${path}`, true);
    xhr.setRequestHeader('Accept', 'application/json');
    const token = getToken();
    if (token) xhr.setRequestHeader('X-Session-Token', token);
    const tid = getActiveTenantId();
    if (tid != null) xhr.setRequestHeader('X-Tenant-Id', tid);

    if (xhr.upload && opts.onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) opts.onProgress!(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onerror = () => reject(new Error('Network error during upload.'));
    xhr.onload = () => {
      if (xhr.status === 401) {
        setToken(null); setActiveTenantId(null);
        window.dispatchEvent(new Event('litigate:logout'));
      }
      const ctype = xhr.getResponseHeader('Content-Type') || '';
      let payload: unknown = xhr.responseText;
      if (ctype.includes('application/json')) {
        try { payload = JSON.parse(xhr.responseText); } catch { /* noop */ }
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(payload as T);
      } else {
        const err = new Error(
          (payload as { error?: { message?: string } })?.error?.message || `Upload failed (${xhr.status}).`,
        ) as Error & { code?: string; status?: number };
        err.code = (payload as { error?: { code?: string } })?.error?.code || `http_${xhr.status}`;
        err.status = xhr.status;
        reject(err);
      }
    };
    xhr.send(form);
  });
}
